import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { INITIAL_LIST_PARAMS } from 'config';
import { DateTypes } from 'types/client';
import {
  Comment,
  CommentGroup,
  CommentListParams,
  CommentState,
  CommentStatus,
} from 'types/comment';
import { Pagination } from 'types/common';
import {
  approveCommentThunk,
  createCommentThunk,
  deleteCommentThunk,
  editCommentThunk,
  getCommentGroupsThunk,
  getCommentListThunk,
  getCommentThunk,
} from './thunks';

const initialState: CommentState = {
  isLoading: false,
  comments: [],
  commentsReview: [],
  meta: null,
  metaReview: null,
  commentListParams: {
    ...INITIAL_LIST_PARAMS,
    isReview: false,
    sort: 'desc',
    sort_by: 'updated_at',
  },
  commentReviewListParams: {
    ...INITIAL_LIST_PARAMS,
    status: [CommentStatus.CANCELLED],
    isReview: true,
    sort: 'desc',
    sort_by: 'updated_at',
  },
  comment: null,
  commentGroups: [],
  filtersListParams: { ...INITIAL_LIST_PARAMS, isReview: false },
  filtersReviewListParams: {
    ...INITIAL_LIST_PARAMS,
    status: [CommentStatus.CANCELLED],
    isReview: true,
  },
  filtersErrors: {
    [DateTypes.CREATED_AT_START]: '',
    [DateTypes.CREATED_AT_END]: '',
  },
  filtersReviewErrors: {
    [DateTypes.CREATED_AT_START]: '',
    [DateTypes.CREATED_AT_END]: '',
  },
};

const slice = createSlice({
  name: 'comment',
  initialState,
  reducers: {
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    resetError(state) {
      state.error = undefined;
    },

    resetComment(state) {
      state.comment = null;
    },

    setCommentListParams(state, action) {
      state.commentListParams = action.payload;
    },

    setCommentReviewListParams(state, action) {
      state.commentReviewListParams = action.payload;
    },

    setFiltersListParams(state, action) {
      state.filtersListParams = action.payload;
    },

    setFiltersReviewListParams(state, action) {
      state.filtersReviewListParams = action.payload;
    },

    setFiltersErrors(state, action) {
      state.filtersErrors = action.payload;
    },

    setFiltersReviewErrors(state, action) {
      state.filtersReviewErrors = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      // getCommentListThunk
      .addCase(getCommentListThunk.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(
        getCommentListThunk.fulfilled,
        (
          state,
          {
            payload,
            meta,
          }: {
            payload: { data: Comment[]; meta: Pagination };
            meta: { arg: CommentListParams };
          }
        ) => {
          const { isReview } = meta.arg;

          if (isReview) {
            state.commentsReview = payload.data;
            state.metaReview = payload.meta;
          } else {
            state.comments = payload.data;
            state.meta = payload.meta;
          }

          state.isLoading = false;
        }
      )
      .addCase(getCommentListThunk.rejected, (state, { payload }: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = payload;
      })

      // getCommentThunk
      .addCase(getCommentThunk.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(getCommentThunk.fulfilled, (state, { payload }: PayloadAction<Comment>) => {
        state.isLoading = false;
        state.comment = payload;
      })
      .addCase(getCommentThunk.rejected, (state, { payload }: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = payload;
      })

      // editCommentThunk
      .addCase(editCommentThunk.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(editCommentThunk.fulfilled, (state, { payload }: PayloadAction<Comment>) => {
        state.comments = state.comments.map((comment) =>
          comment.id === payload.id ? payload : comment
        );
        state.isLoading = false;
      })
      .addCase(editCommentThunk.rejected, (state, { payload }: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = payload;
      })

      // createCommentThunk
      .addCase(createCommentThunk.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(createCommentThunk.fulfilled, (state) => {
        state.isLoading = false;
        state.error = undefined;
      })
      .addCase(createCommentThunk.rejected, (state, { payload }: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = payload;
      })

      // approveCommentThunk
      .addCase(approveCommentThunk.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(approveCommentThunk.fulfilled, (state, { meta }) => {
        state.isLoading = false;

        const { ids, status, isReview } = meta.arg;

        if (isReview) {
          state.commentsReview = state.commentsReview.map((comment) => {
            if (ids.some((id) => id === comment.id)) {
              comment.status = status as CommentStatus;
            }

            return comment;
          });
        } else {
          state.comments = state.comments.map((comment) => {
            if (ids.some((id) => id === comment.id)) {
              comment.status = status as CommentStatus;
            }

            return comment;
          });
        }
      })
      .addCase(approveCommentThunk.rejected, (state, { payload }: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = payload;
      })

      // deleteCommentThunk
      .addCase(deleteCommentThunk.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(deleteCommentThunk.fulfilled, (state, { meta }) => {
        state.isLoading = false;

        const { ids, isReview } = meta.arg;

        if (isReview) {
          state.commentsReview = state.commentsReview.filter((comment) =>
            ids.every((id) => id !== comment.id)
          );
        } else {
          state.comments = state.comments.filter((comment) => ids.every((id) => id !== comment.id));
        }
      })
      .addCase(deleteCommentThunk.rejected, (state, { payload }: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = payload;
      })

      // getCommentGroupsThunk
      .addCase(getCommentGroupsThunk.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(
        getCommentGroupsThunk.fulfilled,
        (state, { payload }: PayloadAction<CommentGroup[]>) => {
          state.isLoading = false;
          state.commentGroups = payload;
        }
      )
      .addCase(getCommentGroupsThunk.rejected, (state, { payload }: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = payload;
      });
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  hasError,
  resetError,
  resetComment,
  setCommentReviewListParams,
  setCommentListParams,
  setFiltersReviewListParams,
  setFiltersListParams,
  setFiltersReviewErrors,
  setFiltersErrors,
} = slice.actions;
